import React, { useContext, useState } from 'react';
import { ListItem, ListItemContent, Box, Chip, CircularProgress } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/mtska-frontend-app-component';
import { useFormatter } from 'src/mtska-frontend-app-component/utils';
import { AppContext } from 'src/mtska-frontend-app-scaffolding';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { downloadDocumentMedia } from 'src/mtska-frontend-data-provider/redux/downloadDocumentMedia';
import { useResolvedMediaSrc } from 'src/mtska-frontend-data-provider/hooks/useResolvedMediaSrc';

const clipDataProviderName = 'propertymanagement/propertyclip';

const statusColor = {
    queued: 'neutral',
    processing: 'primary',
    ready: 'success',
    failed: 'danger',
};

const RelationshipPropertyClip = ({ k, v, callback }) => {
    const { t } = useTranslation();
    const { formatDate } = useFormatter();
    const { userCapabilities } = useContext(AppContext);
    const { doAction } = useDataProvider(clipDataProviderName, false);

    const [retrying, setRetrying] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const videoUrl = v?.video?.url ?? null;
    const { src, ready, loading, isMediaPath } = useResolvedMediaSrc(videoUrl);
    const createdAt = v?.created_at ? formatDate(new Date(v.created_at)) : '-';
    const clipId = v?.id ?? v?._id;

    const isPending = v?.status === 'queued' || v?.status === 'processing';
    const canRetry = userCapabilities?.[clipDataProviderName + '.generateclip'];
    const canDownload = v?.status === 'ready' && Boolean(videoUrl);

    const handleRetry = async () => {
        if (!clipId || retrying) {
            return;
        }

        setRetrying(true);
        try {
            await doAction({
                action: 'retryClip',
                model: clipDataProviderName,
                data: { id: clipId },
            });
            callback?.();
        } finally {
            setRetrying(false);
        }
    };

    const handleDownload = async () => {
        if (!videoUrl || downloading) {
            return;
        }

        setDownloading(true);
        try {
            if (isMediaPath) {
                await downloadDocumentMedia(videoUrl);
                return;
            }

            const link = document.createElement('a');
            link.href = src ?? videoUrl;
            link.download = v?.video?.filename ?? 'clip.mp4';
            link.click();
        } finally {
            setDownloading(false);
        }
    };

    return (
        <ListItem key={k} variant="outlined" className="relationship-element">
            <ListItemContent className="relationship-element-content">
                <Box className="coupled status-col">
                    <Box className="label">{t('Status')}</Box>
                    <Box className="value">
                        <Chip size="sm" color={statusColor[v?.status] ?? 'neutral'} variant="soft">
                            {t(v?.status ?? 'queued')}
                        </Chip>
                    </Box>
                </Box>
                <Box className="coupled date-col">
                    <Box className="label">{t('Created at')}</Box>
                    <Box className="value">{createdAt}</Box>
                </Box>
                <Box className="coupled prompt-col">
                    <Box className="label">{t('Prompt')}</Box>
                    <Box className="value" title={v?.prompt ?? ''}>{v?.prompt ?? '-'}</Box>
                </Box>
                <Box className="coupled clip-actions norightborder">
                    {v?.status === 'failed' && canRetry && (
                        <Button
                            size="small"
                            variant="outlined"
                            icon="faRotateRight"
                            label={t('Retry')}
                            onClick={handleRetry}
                            loading={retrying}
                        />
                    )}
                    {canDownload && (
                        <Button
                            size="small"
                            variant="outlined"
                            icon="faDownload"
                            label={t('Download')}
                            onClick={handleDownload}
                            loading={downloading}
                            disabled={!ready && isMediaPath}
                        />
                    )}
                </Box>
                {v?.status === 'failed' && v?.fail_reason && (
                    <Box className="coupled clip-error clip-row-span">
                        <Box className="label">{t('Error')}</Box>
                        <Box className="value">{v.fail_reason}</Box>
                    </Box>
                )}
                {isPending && (
                    <Box className="coupled clip-progress clip-row-span">
                        <CircularProgress size="sm" />
                        <Box className="value">
                            {v?.status === 'queued'
                                ? t('Clip queued, waiting for generation')
                                : t('Clip generation in progress')}
                        </Box>
                    </Box>
                )}
                {v?.status === 'ready' && videoUrl && (
                    <Box className="coupled clip-media clip-row-span norightborder">
                        <Box className="label">{t('Clip')}</Box>
                        <Box className="value clip-video">
                            {loading && <span>{t('Loading')}...</span>}
                            {ready && src && (
                                <video controls preload="metadata">
                                    <source src={src} type="video/mp4" />
                                </video>
                            )}
                        </Box>
                    </Box>
                )}
            </ListItemContent>
        </ListItem>
    );
};

export default RelationshipPropertyClip;
